<script lang="ts">
  import { without } from 'lodash-es'
  import app from '#app/app'
  import { isNonEmptyArray } from '#app/lib/boolean_tests'
  import Flash, { type FlashState } from '#app/lib/components/flash.svelte'
  import { newError } from '#app/lib/error'
  import { locationStore } from '#app/lib/location_store'
  import { userContent } from '#app/lib/user_content'
  import { isOpenedOutside } from '#app/lib/utils'
  import ImageDiv from '#components/image_div.svelte'
  import Modal from '#components/modal.svelte'
  import AuthorsInline from '#entities/components/layouts/authors_inline.svelte'
  import { formatYearClaim } from '#entities/components/lib/claims_helpers'
  import { getEntityImagePath } from '#entities/lib/entities'
  import { getWorkAuthorsUris } from '#entities/lib/types/work_alt'
  import ListingElementShow from '#listings/components/listing_element_show.svelte'
  import { getListingMetadata, getListingPathname, getElementPathname, getElementMetadata, removeElement, askUserConfirmationAndRemove, type ListingElementWithEntity, type SerializedListing } from '#listings/lib/listings'
  import type { ListingElement } from '#server/types/element'
  import { i18n } from '#user/lib/i18n'
  import type { SerializedUser } from '#users/lib/users'
  import ListingElementActions from './listing_element_actions.svelte'

  let {
    element = $bindable(),
    listing,
    elements = $bindable(),
    isEditable,
    isCreatorMainUser,
    autocompleteFlash = $bindable(),
    creator,
  }: {
    element: ListingElementWithEntity
    listing: SerializedListing
    elements: ListingElement[]
    isEditable: boolean
    isCreatorMainUser: boolean
    autocompleteFlash: FlashState
    creator: SerializedUser
  } = $props()

  const listingId = $derived(listing._id)
  const { entity, _id: elementId } = element
  // svelte-ignore state_referenced_locally
  if (!entity) throw newError('missing element entity', 500, { listingId, elementId })
  const { uri, label, claims, image } = entity
  const publicationYear = formatYearClaim('wdt:P577', claims)[0]
  const authorsUris = getWorkAuthorsUris(entity)

  let imageUrl = $state()
  let flash: FlashState | undefined = $state()

  if (isNonEmptyArray(image)) {
    // This is the case when the entity object is a search result object
    imageUrl = getEntityImagePath(image[0])
  } else if (image?.url) {
    imageUrl = image.url
  }

  function showElement (e) {
    autocompleteFlash = null
    if (isOpenedOutside(e)) return
    const options = { metadata: getElementMetadata(listing, element), preventScrollTop: true }
    // The navigation will trigger an update of showElementModal
    app.navigate(getElementPathname(listingId, elementId), options)
    e.preventDefault()
  }

  function closeElementModal () {
    autocompleteFlash = null
    // The navigation will trigger an update of showElementModal
    navigateToListingPathname()
  }

  // Determining showElementModal from the current location allows to automatically close the modal
  // when navigating back (cf history 'popstate' event)
  const showElementModal = $derived($locationStore.route === getElementPathname(listingId, elementId))

  function navigateToListingPathname () {
    const options = { metadata: getListingMetadata(listing), preventScrollTop: true }
    app.navigate(getListingPathname(listingId), options)
  }

  async function onRemoveElement (element: ListingElementWithEntity) {
    async function _removeElement () {
      await removeElement(listingId, uri)
      navigateToListingPathname()
      // Enhancement: after remove, have an "undo" button
      elements = without(elements, element)
    }
    await askUserConfirmationAndRemove(_removeElement, element?.comment)
      .catch(err => flash = err)
  }

  const index = $derived(elements.findIndex(obj => obj.uri === uri))

  const comment = $derived(element.comment)
</script>

{#if showElementModal}
  <Modal onCloseModal={closeElementModal}>
    <ListingElementShow
      {entity}
      {creator}
      bind:element
      {onRemoveElement}
      {isCreatorMainUser}
    />
  </Modal>
{/if}

<div class="listing-element-wrapper">
  <div class="listing-element">
    <a
      href="/lists/{listingId}/element/{element._id}"
      title={label}
      onclick={showElement}
    >
      {#if imageUrl}
        <ImageDiv
          url={imageUrl}
          size={100}
        />
      {/if}
      <div class="main-text-wrapper">
        <div>
          <span class="label">{label}</span>
          {#if publicationYear}
            <p
              class="publicationYear"
              title={i18n('wdt:P577')}
            >
              {publicationYear}
            </p>
          {/if}
          <AuthorsInline entitiesUris={authorsUris} />
        </div>
        {#if comment}
          <p class="comment">
            {@html userContent(comment.slice(0, 150))}
            {#if comment.length > 150}…{/if}
          </p>
        {/if}
      </div>
    </a>
    {#if isEditable}
      <ListingElementActions
        {element}
        bind:flash
        bind:elements
        {index}
      />
    {/if}
  </div>
  <Flash bind:state={flash} />
</div>

<style lang="scss">
  @import "general/scss/utils";
  .listing-element-wrapper{
    @include display-flex(column, stretch, flex-start);
    width: 100%;
  }
  .listing-element{
    @include display-flex(row, unset, space-between);
    a{
      padding: 1em;
      min-height: 6em;
      @include display-flex(row, center, flex-start);
      cursor: pointer;
      flex: 1;
      :global(.image-div){
        block-size: 6em;
        flex: 0 0 4em;
        margin-inline-end: 1em;
      }
    }
  }
  .main-text-wrapper{
    @include display-flex(column, flex-start);
  }
  .comment{
    background-color: $off-white;
    padding: 0.5em 1em;
    @include radius;
  }
  .label{
    @include serif;
    padding-inline-end: 0.5em;
    font-size: 1.1em;
  }
</style>
